import { useState } from "react";
import { FormControl, Select, Text } from "@chakra-ui/react";
import ConfirmDialog from "@/components/consignado/Cobranca/components/ConfirmDialog";
import { useDisparoTemplates, useTemplateDeOrigem } from "@/hooks/useDisparos";
import {
  CatalogoDisparo,
  ContextoDisparo,
  DisparoTemplateItem,
  ModoTemplate,
  ProvedorComunicacao,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import { CanalDraft, rotuloMensagemPropria } from "./disparo-draft";
import { mensagemDoTemplate } from "./template-draft";
import CampoLabel from "./CampoLabel";
import { fieldFont } from "./styleTokens";

type Props = {
  canal: CanalDraft & { provedor: ProvedorComunicacao };
  contexto: ContextoDisparo;
  catalogo?: CatalogoDisparo;
  onChange: (patch: Partial<CanalDraft>) => void;
};

/** O canal já tem mensagem escrita, que escolher um template substituiria. */
const temMensagemPropria = (c: CanalDraft): boolean =>
  !c.templateId &&
  (c.modo === ModoTemplate.CORPO_PROPRIO
    ? Boolean(c.conteudo.trim() || c.assunto.trim())
    : Boolean(c.identificador.trim() || c.parametros.length));

/**
 * De onde vem a mensagem do canal: escrita aqui ou um template reutilizável.
 *
 * A lista traz só os templates **ativos** com o mesmo contexto, canal e
 * provedor do disparo — é o que o backend aceita (`DisparoConfiguracaoValidator`
 * recusa template inativo ou de outra tripla).
 *
 * O vínculo é vivo: com `templateId`, o backend troca a mensagem do disparo
 * pela do template ao salvar, o detalhe devolve o template atual e o envio
 * também o usa. Por isso a mensagem vinculada é só leitura
 * (TemplateMensagemForm) — editar aqui seria descartado. Voltar para a opção
 * sem template ("Escrever a mensagem aqui"; no WhatsApp, "Informar o template
 * do provedor aqui") desfaz o vínculo e deixa a mensagem própria, editável.
 */
export default function TemplateReutilizavelCampo({ canal, contexto, catalogo, onChange }: Props) {
  const lista = useDisparoTemplates({
    ativo: true,
    contexto,
    canal: canal.canal,
    provedor: canal.provedor,
  });
  const vinculado = useTemplateDeOrigem(canal.templateId);
  const [pendente, setPendente] = useState<DisparoTemplateItem | null>(null);

  const opcoes = lista.data ?? [];
  const naLista = opcoes.some((t) => t.id === canal.templateId);

  // Vínculo que o backend recusaria ao salvar: template desativado, excluído
  // ou de outra tripla (o contexto do disparo pode ter mudado depois).
  const t = vinculado.data;
  const problema = !canal.templateId
    ? null
    : vinculado.isError
      ? "O template vinculado não foi encontrado — pode ter sido excluído."
      : t && !t.ativo
        ? `O template "${t.nome}" está inativo.`
        : t && (t.contexto !== contexto || t.canal !== canal.canal || t.provedor !== canal.provedor)
          ? `O template "${t.nome}" é de ${rotuloContexto(t.contexto, catalogo)}, e não serve para este contexto, canal e provedor.`
          : null;

  const semTemplate = rotuloMensagemPropria(canal.modo);

  const aplicar = (tpl: DisparoTemplateItem) => onChange(mensagemDoTemplate(tpl));

  const handleEscolha = (id: string) => {
    if (!id) {
      onChange({ templateId: undefined });
      return;
    }
    const tpl = opcoes.find((x) => x.id === id);
    if (!tpl) return;
    if (temMensagemPropria(canal)) setPendente(tpl);
    else aplicar(tpl);
  };

  return (
    <FormControl>
      <CampoLabel
        ajuda={
          canal.modo === ModoTemplate.REFERENCIA_EXTERNA
            ? "Informar o template aprovado no provedor só para este disparo, ou usar um template reutilizável do mesmo contexto, canal e provedor."
            : "Escrever a mensagem só para este disparo, ou usar um template reutilizável do mesmo contexto, canal e provedor."
        }
      >
        Origem da mensagem
      </CampoLabel>
      <Select
        {...fieldFont}
        value={canal.templateId ?? ""}
        onChange={(e) => handleEscolha(e.target.value)}
      >
        <option value="">{semTemplate}</option>
        {canal.templateId && !naLista && (
          <option value={canal.templateId} disabled>
            Template: {t?.nome ?? "vinculado"} (indisponível)
          </option>
        )}
        {opcoes.map((tpl) => (
          <option key={tpl.id} value={tpl.id}>
            Template: {tpl.nome}
          </option>
        ))}
      </Select>

      {problema ? (
        <Text fontSize="xs" color="red.600" mt={1} {...fieldFont}>
          {problema} Do jeito que está, o disparo não salva: escolha outro template ou "
          {semTemplate}".
        </Text>
      ) : lista.isError ? (
        <Text fontSize="xs" color="red.600" mt={1} {...fieldFont}>
          Não foi possível carregar os templates.
        </Text>
      ) : !lista.isLoading && opcoes.length === 0 && !canal.templateId ? (
        <Text fontSize="xs" color="gray.500" mt={1} {...fieldFont}>
          Nenhum template ativo para este contexto, canal e provedor.
        </Text>
      ) : null}

      <ConfirmDialog
        isOpen={!!pendente}
        onClose={() => setPendente(null)}
        onConfirm={() => {
          if (pendente) aplicar(pendente);
          setPendente(null);
        }}
        titulo="Usar o template?"
        descricao={`A mensagem escrita neste disparo será substituída pela do template "${pendente?.nome ?? ""}".`}
        confirmLabel="Usar o template"
        cancelLabel="Manter a mensagem"
        colorScheme="green"
      />
    </FormControl>
  );
}
